import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useProjectContext } from '../../contexts/ProjectContext';
import { 
  FolderOpen, 
  ChevronDown, 
  Plus, 
  Loader, 
  Building,
  Globe,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function GlobalProjectSelector() {
  const { 
    selectedProject, 
    projects, 
    isLoading, 
    setSelectedProjectId 
  } = useProjectContext();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    setIsOpen(false);
  };

  const handleCreateProject = () => {
    setIsOpen(false);
    navigate('/projects');
  };

  const getProjectStatus = (project: any) => {
    if (project.ga4PropertyId) {
      return { status: 'connected', icon: CheckCircle, color: 'text-green-600 dark:text-green-400' };
    }
    return { status: 'setup', icon: AlertCircle, color: 'text-yellow-600 dark:text-yellow-400' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <Loader className="w-4 h-4 animate-spin text-gray-500 dark:text-gray-400" />
        <span className="text-sm text-gray-600 dark:text-gray-300">Loading projects...</span>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <Button 
        variant="outline" 
        size="sm"
        onClick={handleCreateProject}
        className="flex items-center space-x-2"
      >
        <Plus className="w-4 h-4" />
        <span className="hidden sm:inline">Create Project</span>
      </Button>
    );
  }

  if (!selectedProject) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
        <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
        <span className="text-sm text-red-600 dark:text-red-400">No project selected</span>
      </div>
    );
  }

  const projectStatus = getProjectStatus(selectedProject);
  const StatusIcon = projectStatus.icon;

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 max-w-48 sm:max-w-64"
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <FolderOpen className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          <div className="flex flex-col items-start min-w-0 flex-1">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate w-full">
              {selectedProject.name}
            </span>
            <div className="flex items-center space-x-1">
              <StatusIcon className={`w-3 h-3 ${projectStatus.color}`} />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {projectStatus.status === 'connected' ? 'GA4 Connected' : 'Setup Required'}
              </span>
            </div>
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      </Button>

      {isOpen && (
        <>
          {/* Overlay to close dropdown when clicking outside */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <div className="absolute top-full right-0 mt-1 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Switch Project
                </h3>
                <Badge variant="secondary" className="text-xs">
                  {projects.length} projects
                </Badge>
              </div>
              
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {projects.map((project) => {
                  const isSelected = project.id === selectedProject?.id;
                  const status = getProjectStatus(project);
                  const ProjectStatusIcon = status.icon;
                  
                  return (
                    <button
                      key={project.id}
                      onClick={() => handleProjectSelect(project.id)}
                      className={`w-full flex items-center p-3 text-left rounded-lg transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className={`text-sm font-medium truncate ${
                            isSelected 
                              ? 'text-blue-900 dark:text-blue-100' 
                              : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {project.name}
                          </p>
                          {isSelected && (
                            <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full flex-shrink-0 ml-2" />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            <ProjectStatusIcon className={`w-3 h-3 ${status.color}`} />
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {status.status === 'connected' ? 'GA4 Connected' : 'Setup Required'}
                            </span>
                          </div>
                          
                          {project.websiteUrl && (
                            <div className="flex items-center space-x-1">
                              <Globe className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                              <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-24">
                                {project.websiteUrl.replace(/^https?:\/\//, '')}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {project.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                            {project.description}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-3">
                <button 
                  className="w-full flex items-center px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  onClick={handleCreateProject}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create new project
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
} 